import { Scripts as AdManagerScripts } from '@client/adManager/Scripts.js';
import { Scripts as AdnamiScripts } from '@client/core/utils/Adnami/Scripts.js';
import { Scripts as BrandMetricsScripts } from '@client/core/utils/BrandMetrics/Scripts.js';
import { Scripts as CogwheelScripts } from '@client/core/utils/Cogwheel/Scripts.js';
import { Scripts as PulseScripts } from '@client/core/utils/pulse/Scripts.js';
import type { Script } from '@client/core/utils/Script.js';
import {
  debugLog,
  type InitialState,
  ConsentStatus
} from '@schibsted-nmp/advertising-shared';

export namespace Scripts {
  export function* get(
    initialState: InitialState,
    consent: ConsentStatus
  ): Generator<Script, void, void> {
    const {
      env,
      config: {
        brand,
        adServer: { gam }
      }
    } = initialState;

    if (env === 'local') {
      yield* PulseScripts.get();
    }

    if (consent === ConsentStatus.Accepted) {
      yield* BrandMetricsScripts.get(brand);
    }

    yield* CogwheelScripts.get();

    if (gam) {
      yield* AdnamiScripts.get(brand);
    }

    yield* AdManagerScripts.get(initialState, false);
  }

  export function* load(
    initialState: InitialState,
    consent: ConsentStatus
  ): Generator<Promise<Event | null>, void, void> {
    for (const script of get(initialState, consent)) {
      yield injectIntoDom(script);
    }
  }

  export async function injectIntoDom({
    name,
    src,
    defer,
    crossOrigin
  }: Script) {
    if (document.getElementById(`advt-script-${name}`)) {
      debugLog(`${name} injected server-side from advertising-podlet`);
      return null;
    }

    const { promise, resolve, reject } = Promise.withResolvers<Event>();
    const script = document.createElement('script');

    const isNonCritical = name === 'brand-metrics';

    Object.assign(script, {
      src,
      type: 'text/javascript',
      async: true,

      ...(defer && { defer }),
      ...(crossOrigin && { crossOrigin }),

      onload(event) {
        debugLog(`${name} loaded from advertising-podlet`, event);
        resolve(event);
      },

      onerror(error) {
        if (isNonCritical) {
          debugLog(`${name} is non-critical – continuing without it`);
          resolve({} as Event);
        }
        debugLog(`Failed to load ${name} from advertising-podlet`, error);
        reject(error);
      }
    } satisfies Partial<typeof script>);

    document.head.appendChild(script);

    return await promise;
  }
}
